import { useEffect, useState } from "react";
import { api, type Settings as S, type UsageSummary } from "../lib/api.ts";
import { Icon } from "../lib/icons.tsx";

const MODELS: Record<string, { id: string; label: string }[]> = {
  anthropic: [
    { id: "claude-sonnet-5", label: "Claude Sonnet 5 (แนะนำ — สมดุล)" },
    { id: "claude-opus-5", label: "Claude Opus 5 (ฉลาดสุด แพงสุด)" },
    { id: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5 (เร็ว ประหยัด)" },
  ],
  mock: [{ id: "mock", label: "Mock (ทดลอง — ไม่มีค่าใช้จ่าย)" }],
};

export default function Settings() {
  const [s, setS] = useState<S | null>(null);
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [provider, setProvider] = useState("mock");
  const [model, setModel] = useState("mock");
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    api.get<S>("/settings").then((r) => {
      setS(r);
      setProvider(r.provider);
      setModel(r.model);
    });
    api.get<UsageSummary>("/ai/usage").then(setUsage);
  }, []);

  async function save() {
    setSaving(true);
    setMsg(null);
    try {
      const payload: any = { provider, model };
      if (apiKey.trim()) payload.apiKey = apiKey.trim();
      const r = await api.put<S>("/settings", payload);
      setS(r);
      setApiKey("");
      setMsg(provider === "anthropic" && (r.hasApiKey) ? "บันทึกแล้ว — ระบบใช้ Claude จริงแล้ว 🎉" : "บันทึกแล้ว");
    } catch {
      setMsg("บันทึกไม่สำเร็จ");
    } finally {
      setSaving(false);
    }
  }

  function clearKey() {
    if (!confirm("ลบ API Key และกลับไปโหมด Mock?")) return;
    api.put<S>("/settings", { provider: "mock", model: "mock", apiKey: "" }).then((r) => {
      setS(r); setProvider("mock"); setModel("mock"); setMsg("ลบ Key แล้ว กลับสู่โหมด Mock");
    });
  }

  return (
    <div className="page">
      <div className="page__head">
        <div className="page__title">ตั้งค่า</div>
        <div className="page__subtitle">เชื่อมต่อ AI และดูค่าใช้จ่าย</div>
      </div>

      <div className="mh-card card-pad" style={{ marginBottom: 18 }}>
        <div className="mh-spread" style={{ marginBottom: 14 }}>
          <h3 style={{ fontSize: 16 }}>สมอง AI (AI Provider)</h3>
          <span className={"mh-pill " + (s?.hasApiKey ? "mh-pill--winner" : "mh-pill--watch")}>
            {s?.hasApiKey ? "เชื่อม Claude แล้ว" : "โหมด Mock"}
          </span>
        </div>

        {!s?.hasApiKey && (
          <div className="mock-banner">
            <Icon name="Wrench" size={18} />
            <div>ตอนนี้ใช้ <b>Mock AI</b> (ตอบด้วยโครงจริง ไม่มีค่าใช้จ่าย) — ใส่ Claude API Key ด้านล่างเพื่อเปิดใช้ AI จริงทั้งระบบทันที รับ key ได้ที่ console.anthropic.com</div>
          </div>
        )}

        <div className="form-grid">
          <div className="mh-field" style={{ margin: 0 }}>
            <label className="mh-label">ผู้ให้บริการ (Provider)</label>
            <select className="mh-select" value={provider} onChange={(e) => { setProvider(e.target.value); setModel(MODELS[e.target.value]?.[0]?.id ?? "mock"); }}>
              <option value="mock">Mock (ทดลอง)</option>
              <option value="anthropic">Anthropic — Claude</option>
            </select>
          </div>
          <div className="mh-field" style={{ margin: 0 }}>
            <label className="mh-label">โมเดล (Model)</label>
            <select className="mh-select" value={model} onChange={(e) => setModel(e.target.value)}>
              {(MODELS[provider] ?? MODELS.mock).map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
        </div>

        {provider === "anthropic" && (
          <div className="mh-field" style={{ marginTop: 14, marginBottom: 0 }}>
            <label className="mh-label">Claude API Key {s?.hasApiKey && <span className="mh-muted">(มีคีย์อยู่แล้ว — เว้นว่างไว้หากไม่เปลี่ยน)</span>}</label>
            <input className="mh-input" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="sk-ant-..." autoComplete="off" />
            <div className="mh-help">คีย์ถูกเก็บฝั่งเซิร์ฟเวอร์ ไม่ถูกส่งกลับมาที่เบราว์เซอร์</div>
          </div>
        )}

        <div style={{ marginTop: 16, display: "flex", gap: 10, alignItems: "center" }}>
          <button className="mh-btn mh-btn--primary" onClick={save} disabled={saving}>{saving ? <span className="spinner" /> : "บันทึกการตั้งค่า"}</button>
          {s?.hasApiKey && <button className="mh-btn mh-btn--ghost" onClick={clearKey}>ลบ Key / กลับโหมด Mock</button>}
          {msg && <span className="mh-pill mh-pill--testing">{msg}</span>}
        </div>
      </div>

      <div className="mh-card card-pad">
        <h3 style={{ fontSize: 16, marginBottom: 14 }}>มิเตอร์ค่าใช้จ่าย AI (Cost Meter)</h3>
        <div className="cards">
          <Stat label="เรียกใช้ทั้งหมด" value={usage ? `${usage.calls} ครั้ง` : "—"} />
          <Stat label="ค่าใช้จ่ายสะสม" value={usage ? `$${usage.totalCostUsd.toFixed(4)}` : "—"} accent />
          <Stat label="Input tokens" value={usage ? usage.inputTokens.toLocaleString() : "—"} />
          <Stat label="Output tokens" value={usage ? usage.outputTokens.toLocaleString() : "—"} />
        </div>
        <div className="mh-help" style={{ marginTop: 12 }}>โหมด Mock ไม่มีค่าใช้จ่าย ($0) — ตัวเลขจะเริ่มนับเมื่อใช้ Claude จริง</div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="mh-card card-pad" style={{ boxShadow: "none", background: "var(--gray-050)" }}>
      <div className="mh-sec" style={{ fontSize: 13 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4, color: accent ? "var(--blue-600)" : "var(--text-primary)" }}>{value}</div>
    </div>
  );
}
